"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

const navLinks = [
  { label: 'Home', href: '/homepage' },
  { label: 'Vision 2047', href: '/vision-2047-overview' },
  { label: 'Reality Tracker', href: '/reality-tracker' },
  { label: 'Economy', href: '/economy' },
  { label: 'Data Library', href: '/data-library' },
  { label: 'Methodology', href: '/methodology' },
];

export default function Header() {
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-200 ${
        scrolled ? 'shadow-header' : 'border-b border-border'
      }`}
    >
      <div className="container-research">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2.5 group">
            <AppLogo size={32} />
            <div className="flex flex-col leading-none">
              <span className="font-heading font-700 text-primary text-base tracking-tight">
                TG2047
              </span>
              <span className="text-[10px] text-muted tracking-wide font-mono hidden sm:block">
                Vision Reality Tracker
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href || pathname?.startsWith(link?.href + '/');
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors duration-150 ${
                    isActive
                      ? 'text-accent bg-accent-light' :'text-foreground hover:text-primary hover:bg-research'
                  }`}
                >
                  {link?.label}
                </Link>
              );
            })}
          </nav>

          {/* Right: Last Updated + Mobile Toggle */}
          <div className="flex items-center gap-3">
            <span className="hidden lg:flex items-center gap-1.5 text-xs text-muted font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-success inline-block"></span>
              Data: Mar 2026
            </span>
            <button
              className="md:hidden p-2 text-primary"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                {mobileOpen ? (
                  <path d="M4 4L16 16M16 4L4 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                ) : (
                  <>
                    <path d="M3 5H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M3 10H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    <path d="M3 15H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>
      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-border">
          <nav className="container-research py-3 flex flex-col gap-1">
            {navLinks?.map((link) => {
              const isActive = pathname === link?.href;
              return (
                <Link
                  key={link?.href}
                  href={link?.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-3 py-2.5 text-sm font-medium rounded transition-colors ${
                    isActive
                      ? 'text-accent bg-accent-light' :'text-foreground hover:bg-research'
                  }`}
                >
                  {link?.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}